import React, { useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import LoginLogo from "../../../../../assets/page/login_logo.svg";

type HeatMapPrintButtonProps = {
  children: React.ReactNode;
  autoPrint?: boolean;
};

export default function HeatMapPrintButton({ children, autoPrint }: HeatMapPrintButtonProps) {
  const componentRef = useRef<HTMLDivElement>(null);

  const now = new Date();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = now.toLocaleString("en-US", {
    timeZone: timeZone,
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
  .replace("at", "");

  const offsetMinutes = -now.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absOffset = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absOffset / 60)).padStart(2, "0");
  const minutes = String(absOffset % 60).padStart(2, "0");
  const offset = `UTC${sign}${hours}:${minutes}`;

  const formattedFooter = `Data as of ${formattedDate} (${timeZone}, ${offset})`;

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    pageStyle: `
    @page {
      size: portrait;
      margin: 10mm 5mm 10mm 5mm;
      @top-right {
          content: "Page " counter(page) " of " counter(pages);
          font-size: 8pt;
          color: #4A5565;
          padding-bottom: -5mm;
          margin-bottom: -5mm;
        }
    }

    body {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    @media print {
      /* CHỪA CHỖ CHO HEADER & FOOTER CHO MỌI TRANG */
      :root {
      --print-header-h: 30mm; /* = height header (12mm) + 2mm an toàn */
      --print-footer-h: 16mm; /* = chiều cao footer/spacer của bạn */
     }
     /* áp dụng cho mọi trang */
     body { 
       padding-bottom: var(--print-footer-h);
       counter-reset: page 1;
     }

      header.print-header {
        position: fixed;
        top: 0mm;
        left: 5mm;
        right: -5.5mm;
        height: 12.5mm;
        display: flex;
        justify-content: flex-end;
        align-items: top;
        font-size: 8pt;
        color: #4A5565;
        background: #fff;
        padding: 0 5mm;
        margin: 0mm;
        box-sizing: border-box;
        /* border: 1px solid black; */
        z-index: 1000;
      }

      .print-header__right {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        line-height: 1.2;
      }

      .print-header__right span {
        max-height: 25px;
        display: block;
        font-size: 8pt;
      }

      .print-page {
        page-break-after: always;
      }

      .print-page:last-child {
        page-break-after: auto;
      }

      .page-info::after {
        content: "Page " counter(page) " of " counter(pages);
      }

      .print-header-spacer {
        height: 14mm;
      }

      footer.print-footer {
        position: fixed;
        left: 5mm;
        right: 5mm;
        bottom: 0mm;
        height: 10mm;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 8pt;
        color: #4A5565;
        background: #fff;
        padding: 0px;
        /* border: 1px solid #000; */
        box-sizing: border-box;
      }

      .print-footer__left { white-space: pre-line; }
      .print-footer__right img { max-width: 200px; max-height: 50px; display: block; }

      .print-footer-spacer { height: 16mm; }

      table { page-break-inside: auto; border-collapse: collapse; }
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      tr, td, th { page-break-inside: avoid; }

      .no-print-transform { transform: none !important; filter: none !important; }

      .print-body {
        margin-top: 16mm; /* cao hơn header một chút */
        margin-bottom: 18mm; /* để không đè footer */
      }
    }

    @media screen {
      header.print-header, footer.print-footer { display: none !important; }
    }
  `,
  });

  useEffect(() => {
    if (autoPrint) {
      const timer = setTimeout(() => {
        handlePrint?.();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [autoPrint, handlePrint]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end mt-4">
        <button
          onClick={handlePrint}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          📄 Print / Save as PDF
        </button>
      </div>

      <div style={{ display: 'block' }} ref={componentRef}>
        <header className="print-header" aria-hidden="true">
          <div className="print-header__right">
            <span>{`${formattedDate} (${timeZone}, ${offset})`}</span>
          </div>
        </header>

        <div className="print-body">
          {children}
        </div>

        <div className="print-footer-spacer" aria-hidden="true" />
        <footer className="print-footer" aria-hidden="true">
          {/* <div className="print-footer__left">
            {`CivicPoints Analytics Report • City of Fairburn\n${formattedFooter}`}
          </div>
          <div className="print-footer__right">
            <img src={LoginLogo}
              alt="CivicPoints"
              style={{ width: '100px' }} />
          </div> */}
          <span style={{marginRight: '5px'}}>Powered by</span>
          <img src={LoginLogo}
              alt="CivicPoints"
              style={{ width: '60px' }} />
        </footer>
      </div>

    </div>
  );
}
